import { useState, useRef } from "react";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, Loader2, ScanLine, X, Check, RotateCcw } from "lucide-react";

/**
 * OCR Scanner Component
 * Allows users to take a photo or upload an image and extract text using AI
 * 
 * @param {string} ocrType - Type of text to recognize: "vin", "license_plate", "mileage", "article_number", "general"
 * @param {function} onResult - Callback with recognized text
 * @param {string} buttonLabel - Label for the scan button (optional)
 * @param {string} buttonVariant - Button variant (optional, default: "outline")
 * @param {string} className - Additional CSS classes (optional)
 */
const OcrScanner = ({ 
  ocrType = "general", 
  onResult, 
  buttonLabel,
  buttonVariant = "outline",
  className = ""
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const typeLabels = {
    vin: "Fahrgestellnummer (VIN)",
    license_plate: "Kennzeichen",
    mileage: "Kilometerstand",
    article_number: "Artikelnummer",
    general: "Text"
  };

  const typeInstructions = {
    vin: "Fotografieren Sie die VIN-Nummer am Fahrzeug (meist an der Windschutzscheibe oder Türrahmen)",
    license_plate: "Fotografieren Sie das Kennzeichen des Fahrzeugs",
    mileage: "Fotografieren Sie den Tacho oder das Display mit dem Kilometerstand",
    article_number: "Fotografieren Sie die Artikelnummer auf dem Ersatzteil oder der Verpackung",
    general: "Fotografieren Sie den Text, den Sie erkennen möchten"
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Nur JPEG, PNG oder WebP Bilder erlaubt");
      return;
    }

    // Convert to base64 and show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      setPreviewImage(base64);
      setResult(null);
    };
    reader.readAsDataURL(file);
  };

  const processImage = async () => {
    if (!previewImage) return;

    setProcessing(true);
    setResult(null);

    try {
      // Extract base64 data (remove data URL prefix)
      const base64Data = previewImage.split(",")[1];

      const response = await api.post("/api/ocr/recognize", {
        image_base64: base64Data,
        ocr_type: ocrType
      });

      if (response.data.success) {
        setResult({
          success: true,
          text: response.data.text
        });
        toast.success("Text erfolgreich erkannt!");
      } else {
        setResult({
          success: false,
          error: response.data.error
        });
        toast.error(response.data.error || "Erkennung fehlgeschlagen");
      }
    } catch (err) {
      console.error("OCR error:", err);
      const errorMsg = err.response?.data?.error || "Fehler bei der Texterkennung";
      setResult({
        success: false,
        error: errorMsg
      });
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const confirmResult = () => {
    if (result?.text && onResult) {
      onResult(result.text);
      resetAndClose();
    }
  };

  const resetAndClose = () => {
    setDialogOpen(false);
    setPreviewImage(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const retake = () => {
    setPreviewImage(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const openFileDialog = (capture = false) => {
    if (fileInputRef.current) {
      if (capture) {
        fileInputRef.current.setAttribute("capture", "environment");
      } else {
        fileInputRef.current.removeAttribute("capture");
      }
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <Button
        type="button"
        variant={buttonVariant}
        size="icon"
        onClick={() => setDialogOpen(true)}
        className={className}
        title={`${typeLabels[ocrType]} scannen`}
      >
        <ScanLine className="w-4 h-4" />
      </Button>

      <Dialog open={dialogOpen} onOpenChange={(open) => {
        if (!open) resetAndClose();
        else setDialogOpen(true);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {typeLabels[ocrType]} scannen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Instructions */}
            <p className="text-sm text-muted-foreground">
              {typeInstructions[ocrType]}
            </p>

            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
            />

            {/* Preview or capture buttons */}
            {!previewImage ? (
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => openFileDialog(true)}
                  className="gap-2 h-24 flex-col"
                  variant="outline"
                >
                  <Camera className="w-8 h-8" />
                  <span>Foto aufnehmen</span>
                </Button>
                <Button
                  onClick={() => openFileDialog(false)}
                  variant="ghost"
                  className="text-muted-foreground"
                >
                  Oder Bild aus Galerie wählen
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Image preview */}
                <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                  <img
                    src={previewImage}
                    alt="Vorschau"
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Result display */}
                {result && (
                  <div className={`p-4 rounded-lg ${
                    result.success 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    {result.success ? (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Erkannt:</p>
                        <p className="text-xl font-mono font-bold text-green-700">
                          {result.text}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-red-700">{result.error}</p>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2">
                  {!result ? (
                    <>
                      <Button
                        onClick={retake}
                        variant="outline"
                        className="flex-1 gap-2"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Neu aufnehmen
                      </Button>
                      <Button
                        onClick={processImage}
                        disabled={processing}
                        className="flex-1 gap-2"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Wird erkannt...
                          </>
                        ) : (
                          <>
                            <ScanLine className="w-4 h-4" />
                            Erkennen
                          </>
                        )}
                      </Button>
                    </>
                  ) : result.success ? (
                    <>
                      <Button
                        onClick={retake}
                        variant="outline"
                        className="flex-1 gap-2"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Neu aufnehmen
                      </Button>
                      <Button
                        onClick={confirmResult}
                        className="flex-1 gap-2 bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-4 h-4" />
                        Übernehmen
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={retake}
                      className="w-full gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Erneut versuchen
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OcrScanner;
